import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";
import {
  Button,
  ButtonVariant,
  LoadingBar,
  RadioButton,
  TextInput,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";

type PayoutItem = NonNullable<
  Awaited<ReturnType<typeof api.getCompanyRyftPayouts>>["payouts"]
>[number];

type PayoutMethod = NonNullable<
  Awaited<ReturnType<typeof api.getCompanyRyftPayoutMethods>>["payoutMethods"]
>[number];

// ─── Styled ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const Section = styled.div`
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
`;

const SectionTitle = styled(Typography)`
  font-weight: 700;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
`;

const RadioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const RadioItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HintText = styled(Typography)`
  color: #8a9bb0;
`;

const DescText = styled(Typography)`
  color: #6b7a8d;
  line-height: 1.5;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7a8d;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
`;

const Td = styled.td`
  padding: 10px 10px;
  border-bottom: 1px solid #f5f5f5;
  color: #2c3e50;
`;

const payoutStatus = (status: string) => status.toLowerCase();

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ status }) => {
    const s = payoutStatus(status);
    return s === "completed"
      ? "#e6f4ea"
      : s === "failed" || s === "returned"
      ? "#fdecea"
      : s === "inprogress" ||
        s === "pendingaccountverification" ||
        s === "pendingreview"
      ? "#fff8e1"
      : "#f5f5f5";
  }};
  color: ${({ status }) => {
    const s = payoutStatus(status);
    return s === "completed"
      ? "#1e7e34"
      : s === "failed" || s === "returned"
      ? "#c62828"
      : s === "inprogress" ||
        s === "pendingaccountverification" ||
        s === "pendingreview"
      ? "#b45309"
      : "#666";
  }};
`;

const EmptyMsg = styled.div`
  padding: 24px;
  text-align: center;
  color: #8a9bb0;
  font-size: 13px;
`;

const LoadMore = styled.div`
  text-align: center;
  padding: 12px 0;
`;

const RYFT_MANUAL_PAYOUT_MIN_POUNDS = 5;

const fmt = (pence?: number) =>
  pence != null ? `£${(pence / 100).toFixed(2)}` : "—";

const fmtDate = (ts?: number) =>
  ts ? DateTime.fromSeconds(ts).toFormat("dd MMM yyyy") : "—";

/** Ryft has no single feeAmount; derive from documented fee fields. */
const getPayoutFeePence = (
  calc?: PayoutItem["payoutCalculation"]
): number | null => {
  if (!calc) return null;
  if (typeof calc.feeAmount === "number") return calc.feeAmount;
  const processing = calc.processingFeesPaidAmount ?? 0;
  const platform = calc.platformFeesPaidAmount ?? 0;
  const sum = processing + platform;
  return sum > 0 ? sum : null;
};

const RYFT_STATUS_APPROVED = "Approved";

type RyftAccountSnapshot = {
  ryft_account_id?: string;
  ryft_account_status?: string;
  frozen?: boolean;
  ryftCardPaymentsEnabled?: boolean;
  ryftPaymentStatus?: string;
  card_payments_enabled?: boolean;
  payout_schedule?: "manual" | "automatic";
};

function getRyftPayoutReadEligibility(
  ryft: RyftAccountSnapshot | null | undefined
): { eligible: boolean; reason?: string } {
  if (!ryft) {
    return {
      eligible: false,
      reason: "No Fena Card Payments account found for this company.",
    };
  }
  if (!ryft.ryft_account_id) {
    return {
      eligible: false,
      reason: "Fena Card Payments account is not connected.",
    };
  }
  return { eligible: true };
}

function getRyftPayoutWriteEligibility(
  ryft: RyftAccountSnapshot | null | undefined
): { eligible: boolean; reason?: string } {
  const read = getRyftPayoutReadEligibility(ryft);
  if (!read.eligible) return read;
  if (ryft!.ryft_account_status !== RYFT_STATUS_APPROVED) {
    return {
      eligible: false,
      reason: "Fena Card Payments account is not approved yet.",
    };
  }
  const cardPaymentsActive =
    ryft!.ryftCardPaymentsEnabled === true &&
    ryft!.ryftPaymentStatus === "enabled";
  if (!cardPaymentsActive && !ryft!.card_payments_enabled) {
    return {
      eligible: false,
      reason: "Card payments are not enabled for this company.",
    };
  }
  if (ryft!.frozen) {
    return { eligible: false, reason: "Fena Card Payments account is frozen." };
  }
  return { eligible: true };
}

const IneligibleBanner = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  color: #6b5a00;
  font-size: 13px;
  line-height: 1.5;
`;

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  companyId: string;
}

export const CompanyRyftPayoutsTab: React.FC<Props> = ({ companyId }) => {
  const { showNotification } = useNotification();

  // Schedule
  const [schedule, setSchedule] = useState<"manual" | "automatic">("manual");
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Payouts list
  const [payouts, setPayouts] = useState<PayoutItem[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);

  // Payout methods
  const [payoutMethods, setPayoutMethods] = useState<PayoutMethod[]>([]);

  // Manual payout modal state
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [amountError, setAmountError] = useState<string | null>(null);
  const [selectedMethodId, setSelectedMethodId] = useState("");
  const [submittingPayout, setSubmittingPayout] = useState(false);

  const [loading, setLoading] = useState(true);
  const [methodsLoadError, setMethodsLoadError] = useState<string | null>(null);
  const [canReadPayouts, setCanReadPayouts] = useState(false);
  const [canManagePayouts, setCanManagePayouts] = useState(false);
  const [readBlockedReason, setReadBlockedReason] = useState<string | null>(
    null
  );
  const [manageBlockedReason, setManageBlockedReason] = useState<string | null>(
    null
  );

  const validPayoutMethods = payoutMethods.filter(
    (m) => payoutStatus(m.status ?? "") === "valid"
  );

  const load = useCallback(async () => {
    setLoading(true);
    setMethodsLoadError(null);
    setPayouts([]);
    setPayoutMethods([]);
    setNextToken(undefined);
    try {
      let ryftSnapshot: RyftAccountSnapshot | null = null;
      try {
        ryftSnapshot = await api.getCompanyRyftData(companyId);
        if (!ryftSnapshot) {
          setCanReadPayouts(false);
          setCanManagePayouts(false);
          setReadBlockedReason(
            "No Fena Card Payments account found for this company."
          );
          return;
        }
        setSchedule(ryftSnapshot.payout_schedule ?? "manual");
      } catch (e: unknown) {
        const message = getApiErrorMessage(e, "Could not load Ryft account");
        setCanReadPayouts(false);
        setCanManagePayouts(false);
        setReadBlockedReason(message);
        return;
      }

      const readEligibility = getRyftPayoutReadEligibility(ryftSnapshot);
      const writeEligibility = getRyftPayoutWriteEligibility(ryftSnapshot);
      setCanReadPayouts(readEligibility.eligible);
      setCanManagePayouts(writeEligibility.eligible);
      setReadBlockedReason(readEligibility.reason ?? null);
      setManageBlockedReason(writeEligibility.reason ?? null);

      if (readEligibility.eligible) {
        try {
          const payoutsData = await api.getCompanyRyftPayouts(companyId, {
            limit: 20,
          });
          setPayouts(payoutsData.payouts ?? payoutsData.items ?? []);
          setNextToken(
            payoutsData.paginationToken ?? payoutsData.nextPaginationToken
          );
        } catch (e: unknown) {
          showNotification(
            getApiErrorMessage(e, "Could not load payout history"),
            NotificationTypes.WARNING
          );
        }
      }

      if (writeEligibility.eligible) {
        try {
          const methodsData = await api.getCompanyRyftPayoutMethods(companyId);
          const methods =
            methodsData?.payoutMethods ?? methodsData?.items ?? [];
          setPayoutMethods(methods);
          const valid = methods.find(
            (m) => payoutStatus(m.status ?? "") === "valid"
          );
          if (valid) setSelectedMethodId(valid.id);
        } catch (e: unknown) {
          const message = getApiErrorMessage(
            e,
            "Could not load payout methods"
          );
          setMethodsLoadError(message);
          showNotification(message, NotificationTypes.WARNING);
        }
      }
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Could not load payout data"),
        NotificationTypes.WARNING
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, showNotification]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSaveSchedule = async (val: "manual" | "automatic") => {
    if (!canManagePayouts) {
      showNotification(
        manageBlockedReason ??
          "Payout management is not available for this account.",
        NotificationTypes.WARNING
      );
      return;
    }
    setSavingSchedule(true);
    try {
      await api.setCompanyPayoutSchedule(companyId, val);
      setSchedule(val);
      showNotification("Payout schedule updated", NotificationTypes.SUCCESS);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Failed to update schedule"),
        NotificationTypes.WARNING
      );
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleLoadMore = async () => {
    if (!canReadPayouts || !nextToken) return;
    setLoadingMore(true);
    try {
      const data = await api.getCompanyRyftPayouts(companyId, {
        limit: 20,
        paginationToken: nextToken,
      });
      setPayouts((prev) => [...prev, ...(data.payouts ?? [])]);
      setNextToken(data.paginationToken);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Could not load more payouts"),
        NotificationTypes.WARNING
      );
    } finally {
      setLoadingMore(false);
    }
  };

  const validatePayoutAmount = (value: string): string | null => {
    const num = parseFloat(value);
    if (!value || !Number.isFinite(num) || num <= 0)
      return "Enter a valid amount (e.g. £100.00)";
    if (num < RYFT_MANUAL_PAYOUT_MIN_POUNDS)
      return `Amount must be at least £${RYFT_MANUAL_PAYOUT_MIN_POUNDS.toFixed(
        2
      )}`;
    if (num > 2000000) return "Amount must not exceed £2,000,000.00";
    return null;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPayoutAmount(val);
    if (amountError) setAmountError(validatePayoutAmount(val));
  };

  const handleCreatePayout = async () => {
    if (!canManagePayouts) {
      showNotification(
        manageBlockedReason ??
          "Payout management is not available for this account.",
        NotificationTypes.WARNING
      );
      return;
    }
    const err = validatePayoutAmount(payoutAmount);
    if (err) {
      setAmountError(err);
      return;
    }
    const pence = Math.round(parseFloat(payoutAmount) * 100);
    if (!selectedMethodId) {
      showNotification("Select a payout method", NotificationTypes.WARNING);
      return;
    }
    setSubmittingPayout(true);
    try {
      await api.createCompanyRyftPayout(companyId, {
        amount: pence,
        currency: "GBP",
        payoutMethodId: selectedMethodId,
      });
      showNotification("Manual payout submitted", NotificationTypes.SUCCESS);
      setShowPayoutForm(false);
      setPayoutAmount("");
      setAmountError(null);
      void load();
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Payout failed"),
        NotificationTypes.WARNING
      );
    } finally {
      setSubmittingPayout(false);
    }
  };

  if (loading) return <LoadingBar loading />;

  return (
    <Wrap>
      {!canReadPayouts && readBlockedReason && (
        <IneligibleBanner>
          Payout history is unavailable: {readBlockedReason}
        </IneligibleBanner>
      )}
      {canReadPayouts && !canManagePayouts && manageBlockedReason && (
        <IneligibleBanner>
          Payout history is shown below (read-only). Schedule changes and manual
          payouts are disabled: {manageBlockedReason}
        </IneligibleBanner>
      )}

      {/* ─ Schedule control ─ */}
      <Section>
        <SectionTitle variant={TypographyVariant.HEADER3}>
          Payout schedule
        </SectionTitle>
        <DescText variant={TypographyVariant.BODY4}>
          Controls when Ryft sends funds from the merchant sub-account to their
          bank. Per Ryft: <strong>Automatic</strong> creates daily payouts;
          <strong> Manual</strong> requires an explicit payout request (below).
          Stored on this company&apos;s Ryft account settings.
        </DescText>
        <RadioRow>
          <RadioItem>
            <RadioButton
              name="admin-payout-schedule"
              checked={schedule === "automatic"}
              labelComponent="Automatic (daily)"
              onChange={() => void handleSaveSchedule("automatic")}
              inputProps={{ disabled: savingSchedule || !canManagePayouts }}
            />
          </RadioItem>
          <RadioItem>
            <RadioButton
              name="admin-payout-schedule"
              checked={schedule === "manual"}
              labelComponent="Manual only"
              onChange={() => void handleSaveSchedule("manual")}
              inputProps={{ disabled: savingSchedule || !canManagePayouts }}
            />
          </RadioItem>
        </RadioRow>
        {savingSchedule && (
          <HintText variant={TypographyVariant.BODY4}>Saving…</HintText>
        )}
      </Section>

      {/* ─ Manual payout trigger ─ */}
      {canManagePayouts && schedule === "manual" && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Request manual payout
          </SectionTitle>
          <DescText variant={TypographyVariant.BODY4}>
            Only available when schedule is Manual. Calls Ryft{" "}
            <code>POST /payouts</code> with amount (pence), currency, and a
            payout method in <code>Valid</code> status. Ryft validates minimum
            balance, verification, and terms acceptance on the sub-account.
          </DescText>
          {methodsLoadError ? (
            <HintText variant={TypographyVariant.BODY4}>
              Could not load payout methods from Ryft ({methodsLoadError}).
              Ensure toolkit-admin-backend has TOOLKIT_BACKEND_URL and
              ADMIN_SERVICE_SECRET configured (same secret on toolkit-backend).
            </HintText>
          ) : validPayoutMethods.length === 0 ? (
            <HintText variant={TypographyVariant.BODY4}>
              No valid payout methods on file. The merchant must add a verified
              bank account in the toolkit (Ryft payout method).
            </HintText>
          ) : (
            <>
              {!showPayoutForm ? (
                <Button
                  variant={ButtonVariant.OUTLINED}
                  onClick={() => setShowPayoutForm(true)}
                >
                  Request payout
                </Button>
              ) : (
                <>
                  {validPayoutMethods.length > 1 && (
                    <div>
                      <Typography
                        variant={TypographyVariant.BODY4}
                        style={{ marginBottom: 6, fontWeight: 600 }}
                      >
                        Destination
                      </Typography>
                      <select
                        value={selectedMethodId}
                        onChange={(e) => setSelectedMethodId(e.target.value)}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 6,
                          border: "1px solid #ddd",
                          fontSize: 13,
                        }}
                      >
                        {validPayoutMethods.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.displayName ?? m.id} ({m.currency})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <TextInput
                    label="Amount (£)"
                    type="number"
                    min={RYFT_MANUAL_PAYOUT_MIN_POUNDS}
                    step="0.01"
                    placeholder="0.00"
                    value={payoutAmount}
                    onChange={handleAmountChange}
                    error={amountError ?? undefined}
                  />
                  <div style={{ display: "flex", gap: 10 }}>
                    <Button
                      variant={ButtonVariant.CONTAINED}
                      onClick={() => void handleCreatePayout()}
                      loading={submittingPayout}
                      loadingText="Submitting"
                    >
                      Submit payout
                    </Button>
                    <Button
                      variant={ButtonVariant.OUTLINED}
                      onClick={() => setShowPayoutForm(false)}
                      disabled={submittingPayout}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </Section>
      )}

      {/* ─ Payout history ─ */}
      <Section>
        <SectionTitle variant={TypographyVariant.HEADER3}>
          Payout history
        </SectionTitle>
        <DescText variant={TypographyVariant.BODY4}>
          Live data from Ryft for this sub-account (same source as the merchant
          toolkit payouts page). Gross amount, Ryft payout fee, and net paid to
          the bank are shown per payout.
        </DescText>
        {payouts.length === 0 ? (
          <EmptyMsg>No payouts yet</EmptyMsg>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Status</Th>
                  <Th>Date</Th>
                  <Th>Gross</Th>
                  <Th>Fee</Th>
                  <Th>Net</Th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id}>
                    <Td>
                      <StatusBadge status={p.status}>{p.status}</StatusBadge>
                    </Td>
                    <Td>{fmtDate(p.createdTimestamp)}</Td>
                    <Td>{fmt(p.amount)}</Td>
                    <Td style={{ color: "#e74c3c" }}>
                      {(() => {
                        const feePence = getPayoutFeePence(p.payoutCalculation);
                        return feePence != null ? `-${fmt(feePence)}` : "—";
                      })()}
                    </Td>
                    <Td style={{ fontWeight: 600 }}>
                      {fmt(p.payoutCalculation?.payoutAmount ?? p.amount)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {nextToken && (
              <LoadMore>
                <Button
                  variant={ButtonVariant.OUTLINED}
                  onClick={() => void handleLoadMore()}
                  loading={loadingMore}
                >
                  Load more
                </Button>
              </LoadMore>
            )}
          </>
        )}
      </Section>
    </Wrap>
  );
};
